import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';
import { environment } from 'environments/environment';
import { User, UserChanges, UserGroups } from '@shared/models/user/user';
import { Region } from '@shared/models/region';
import { Site, SiteBasic } from '@shared/models/user/site';
import { Company } from 'app/core/models/company';
import { Langue } from 'app/core/models/langue';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private apiUrl = environment.vibrationApiURL + '/users';

    constructor(private http: HttpClient) { }

    /**
     * Retrieves a user by their ID from the backend API.
     * @param userId The unique identifier of the user (Keycloak user ID).
     * @returns An Observable that emits the User object or null if not found.
     */
    getUser(userId: string): Observable<User> {
        return this.http.get<User>(`${this.apiUrl}/${userId}`);
    }

    /**
     * Retrieves users.
     */
    getUsers(): Observable<User[]> {
        return this.http.get<User[]>(`${this.apiUrl}`);
    }

    /**
     * Retrieves all areas associated with a specific user.
     * @param userId The unique identifier of the user.
     * @returns An Observable that emits an array of Region objects.
     */
    getUserAreas(userId: string): Observable<Region[]> {
        return this.http.get<Region[]>(`${this.apiUrl}/${userId}/areas`).pipe(
            catchError(() => {
                return of([]);
            })
        );
    }

    /**
     * Retrieves all sites associated with a specific user.
     * @param userId The unique identifier of the user.
     * @returns An Observable that emits an array of SiteBasic objects.
     */
    getUserSites(userId: string): Observable<SiteBasic[]> {
        const url = `${this.apiUrl}/${userId}/sites`;
        return this.http.get<SiteBasic[]>(url).pipe(
            catchError(() => {
                return of([]);
            })
        );
    }

    /**
     * Retrieves all groups associated with a specific user.
     * @param userId The unique identifier of the user.
     * @returns An Observable that emits a UserGroups object.
     */
    getUserGroups(userId: string): Observable<UserGroups> {
        return this.http.get<UserGroups>(`${this.apiUrl}/${userId}/groups`).pipe(
            catchError(() => {
                return of(null);
            })
        );
    }

    /**
     * Retrieves the current authenticated user.
     * @returns An Observable that emits the User object of the current user.
     */
    getCurrentUser(): Observable<User> {
        return this.http.get<User>(`${this.apiUrl}/CurrentUser`);
    }

    /**
     * Retrieves all vertex groups for the current user.
     * @returns An Observable that emits a UserGroups object.
     */
    getVertexGroups(): Observable<UserGroups> {
        return this.http.get<UserGroups>(`${this.apiUrl}/groups`).pipe(
            catchError(() => of(null))
        );
    }

    /**
     * Retrieves all available languages.
     * @returns An Observable that emits an array of Langue objects.
     */
    getLanguages(): Observable<Langue[]> {
        return this.http.get<Langue[]>(`${this.apiUrl}/languages`).pipe(
            catchError(() => of([]))
        );
    }

    /**
     * Retrieves all companies accessible to the user.
     * @returns An Observable that emits an array of Company objects.
     */
    getAllCompanies(): Observable<Company[]> {
        return this.http.get<Company[]>(`${this.apiUrl}/Companies/by-region`).pipe(
            catchError(() => of([]))
        );
    }

    /**
     * Retrieves regions for the current user.
     * @returns An Observable that emits an array of Region objects.
     */
    getRegionsByCurrentUser(): Observable<Region[]> {
        return this.http.get<Region[]>(`${this.apiUrl}/Regions/GetByCurrentUser`).pipe(
            catchError(() => of([]))
        );
    }

    /**
     * Retrieves all sites accessible to the user based on their role and permissions.
     * @returns An Observable that emits an array of Site objects.
     */
    getAllSites(): Observable<Site[]> {
        return this.http.get<Site[]>(`${this.apiUrl}/Sites`).pipe(
            catchError(() => of([]))
        );
    }

    /**
    * Creates a new user by sending user data to the backend API.
    * @param userDto The user data to create, wrapped in a UserChangesDto.
    * @returns An Observable that emits the created User object.
    */
    createUser(userDto: UserChanges): Observable<User> {
        return this.http.post<User>(`${this.apiUrl}`, userDto).pipe(
            catchError((error) => {
                console.error('Error creating user:', error);
                return of(null);
            })
        );
    }

    /**
     * Updates an existing user by sending updated user data to the backend API.
     * @param userId The unique identifier of the user to update.
     * @param userDto The updated user data, wrapped in a UserChangesDto.
     * @returns An Observable that emits a boolean indicating success or failure.
     */
    updateUser(userId: string, userDto: UserChanges): Observable<boolean> {
        return this.http.put<boolean>(`${this.apiUrl}/${userId}`, userDto).pipe(
            catchError((error) => {
                console.error('Error updating user:', error);
                return of(null);
            })
        );
    }
}